(ns frontend.view.nev.kyc.kyc-process
    (:require
      [common.localization :as loc]
      [common.schema.entities.kyc.options :as kyc-options]
      [common.sync-progress :as sync-progress]
      [frontend.actions.entity :as entity-actions]
      [frontend.logging :as logging]
      [frontend.routing :as routing]
      [frontend.state.current-session :as session]
      [frontend.updates.entity-scope :as entity-scope]
      [frontend.utils :as fu]
      [frontend.view.entity-viewers.scope :as scope]
      [frontend.view.entity-viewers.utils :as entity-utils]
      [frontend.view.modals.confirmation.view :as confirmation-modal]
      [frontend.view.modals.error-message.view :as error-message-modal]
      [frontend.view.modals.events :as modals]
      [frontend.view.nev.kyc.process-events :as process-events]
      [frontend.view.nev.kyc.process-status-views.approved-modified-status :as
       approved-modified-status]
      [frontend.view.nev.kyc.process-status-views.approved-status :as approved-status]
      [frontend.view.nev.kyc.process-status-views.draft-status :as draft-status]
      [frontend.view.nev.kyc.process-status-views.esign-in-progress :as esign-in-progress-status]
      [frontend.view.nev.kyc.process-status-views.prefilled-status :as prefilled-status]
      [frontend.view.nev.kyc.process-status-views.rejected-status :as rejected-status]
      [frontend.view.nev.kyc.process-status-views.signatures-status :as signatures-status]
      [frontend.view.nev.kyc.process-status-views.submitted-status :as submitted-status]
      [frontend.view.nev.kyc.process-status-views.template-status :as template-status]
      [frontend.view.nev.kyc.process-status-views.waiting-for-esign-status :as
       waiting-for-esign-status]
      [frontend.view.nev.kyc.process-status-views.wip-status :as wip-status]
      [frontend.view.nev.kyc.process.screening-profiles.event :as screening-profiles-event]
      [frontend.view.nev.kyc.process.screening-profiles.sub :as screening-profiles-sub]
      [frontend.view.not-found :as not-found]
      [frontend.view.syncing-data :as syncing-data]
      [re-frame.core :as rf]
      [reagent.core :as r]
      [ui.components.alert.view :as alert]))


(defn confirm-state-change
  [entity _old-status new-status]
  (modals/open-modal!
   confirmation-modal/modal-key
   {:body           [:p
                     "Are you sure you want to change the status to " (loc/tr new-status)]
    :skip-checkbox? true
    :body-class     "text"
    :confirm-text   (loc/tr :yes)
    :on-confirm     (fn [_]
                      (entity-actions/*->field-update!
                       (:db/id entity)
                       :kyc-process/status
                       new-status
                       identity
                       (fn [err]
                         (logging/error "Failed to update KYC process status " err))))}))

(defn clickable?
  [kyc-process opt]
  (< (->> :kyc-process.status/template
          (kyc-options/get-process-status-option)
          :ord)
     (:ord opt)
     (->> kyc-process
          :kyc-process/status
          (kyc-options/get-process-status-option)
          :ord)))


(defn status
  [entity]
  (let [current-status-opt (kyc-options/get-process-status-option (:kyc-process/status entity))
        click-handler (fn [opt]
                        (when (clickable? entity opt)
                          (confirm-state-change entity (:kyc-process/status entity) (:key opt))))]
    [:div.d-flex.justify-content-center.pt-3
     #_[button/secondary-open
        {:on-click (fn [_]
                     (js/console.log "Reload process"))}
        [:i.fal.fa-redo]]
     [:ul.state-indicator
      (->> kyc-options/process-status-options
           (sort-by :ord)
           (map (fn [opt]
                  [:li
                   {:key        (str "kyc-process-indicator-" (name (:key opt)))
                    :class-name (fu/cs "state-indicator__item"
                                       (when (<= (:ord opt) (:ord current-status-opt))
                                         "state-indicator__item--completed")
                                       (when (clickable? entity opt)
                                         "clickable"))
                    :on-click   (partial click-handler opt)}
                   (->> opt
                        :label)]))
           (doall))]]))

(defn unknown-view
  [_entity]
  [:div.container.pt-4.text-center
   [alert/info
    {}
    (loc/tr :unknown-status)]])

(defn kyc-process-view
  [{:keys [kyc-process-id] :as _view-args}]
  (let [state-atom (r/atom nil)
        ;; Loading state of the entity-scope. The scope puts the process, its profile
        ;; and their neighbors in the session. A new-grid account does not get them
        ;; from the sync, and this page reads the process from the session.
        scope-state (r/atom nil)
        ;; A callback of the backend can arrive after the page has unmounted.
        mounted?* (atom false)
        esign-process-state (r/atom nil)
        signatories-state (r/atom nil)
        local-atom (r/atom {})
        loading-enrichment-data-atom (r/atom {:attributes-to-refresh {}})
        approved-modified-edit-mode?* (r/atom false)
        field-errors-atom (r/atom {})
        close-subscription! (fn [on-done!]
                              (println "Close KYC subscription")
                              (process-events/close-subscription! kyc-process-id on-done!))
        open-subscription!
        (fn []
          (println "OPEN KYC subscription")
          (process-events/open-process-subscription!
           kyc-process-id
           (fn [kyc-process esign-process signatory-updates]
             (js/console.log "Received KYC updates : " kyc-process " \n" esign-process "\n" signatory-updates)
             (if @state-atom
               (do
                 (logging/debug
                  "KYC process updates - SKIP answers - we do not accept any updates to state while user is editing profile")
                 (reset! state-atom (assoc kyc-process
                                           :kyc-process/answers
                                           (:kyc-process/answers @state-atom))))
               (do
                 (logging/debug "Received KYC process " kyc-process-id)
                 (reset! state-atom kyc-process)
                 (reset! local-atom {:email (:kyc-process/invitee-email kyc-process)
                                     :phone (:kyc-process/invitee-phone kyc-process)})))
             (reset! esign-process-state esign-process)
             (swap!
              signatories-state
              (fn [signatories]
                (->> (merge
                      (group-by :db/id signatories)
                      (group-by :db/id signatory-updates))
                     (vals)
                     (map last)
                     (filter (comp not :common/disabled?))))))
           nil))]                                           ; used to keep track which sections are loading enrichment data and also to indicated which field values need to be refreshed in ui
    (r/create-class
     {:component-did-mount
      (fn [_]
        (reset! mounted?* true)
        (entity-utils/reset-state! scope-state kyc-process-id
                                   {:always-fetch? (session/new-grid-2023?)})
        ;; A KYC process has no pointer to its KYC profile: the profile points at
        ;; the process. The backend sends the profile with the process, because
        ;; the scope follows reverse pointers from its root.
        (if (int? kyc-process-id)
          (entity-scope/load-entity!
           {:entity-id kyc-process-id
            :on-loaded (fn [_entity entities]
                         (when @mounted?*
                           (rf/dispatch [::screening-profiles-event/profile-id-resolved
                                         {:kyc-process-id kyc-process-id
                                          :entities       entities}])))
            :on-error  (fn [error]
                         (logging/error "KYC process load failed : " kyc-process-id " >> " error)
                         (when @mounted?*
                           (rf/dispatch [::screening-profiles-event/profile-id-failed
                                         {:kyc-process-id kyc-process-id
                                          :error          error}])))})
          (rf/dispatch [::screening-profiles-event/profile-id-failed
                        {:kyc-process-id kyc-process-id
                         :error          {:error :no-process-id}}]))
        (open-subscription!))
      :component-will-unmount
      (fn [_]
        (reset! mounted?* false)
        (rf/dispatch [::screening-profiles-event/cleared
                      {:kyc-process-id kyc-process-id}])
        (entity-utils/clear-state! scope-state)
        (close-subscription! nil))
      :reagent-render
      (fn [{:keys [kyc-process-id] :as _view-args}]
        (cond
          (:not-found @scope-state)
          [not-found/not-found-content]

          (or (empty? @state-atom)
              (:loading @scope-state)
              (nil? (session/get-entity kyc-process-id)))
          [syncing-data/view]

          :else
          (let [kyc-process (session/get-entity kyc-process-id)
                kyc-profile-id @(rf/subscribe [::screening-profiles-sub/profile-id kyc-process-id])
                esign-process @esign-process-state
                signatories @signatories-state
                ;; do not override with empty answers or nil answers
                ;; Investigate why IndexedDb is cleared or corrupted ??
                save! (fn []
                        (let [answers (:kyc-process/answers @state-atom)]
                          (if (empty? answers)
                            (logging/error "Trying to clear all answers : " @state-atom)
                            (entity-actions/*->field-update!
                             kyc-process-id
                             :kyc-process/answers
                             (pr-str (:kyc-process/answers @state-atom))
                             (fn [_success]
                               (close-subscription! open-subscription!))
                             (fn [_error]
                               (modals/open-modal!
                                error-message-modal/modal-key
                                {:title          (loc/tr :attention)
                                 :body-content   (loc/tr :failed-to-save-form)
                                 :ok-button-text (loc/tr :ok)}))))))]
            ;; The CRM prefill on "Continue" needs the legal entity of the investor.
            ;; It is three hops from the process, so the process scope does not
            ;; reach it. A second scope on the containing profile does reach it
            ;; (profile, investor and legal entity are 0, 1 and 2 hops). This scope
            ;; also keeps the other processes of the profile.
            ;; The screening table gets its screening profiles from this scope too.
            ;; A scope follows reverse pointers from its root only, and the screening
            ;; profiles point at the profile. The scope runs for every user, because
            ;; the table does not read the bulk sync.
            [scope/host
             {:entity-id   kyc-profile-id
              :instance-id :kyc-process-profile
              :on-message-summary (fn [{:keys [upserts removed-ids snapshot?]}]
                                    (rf/dispatch [::screening-profiles-event/entities-received
                                                  {:kyc-profile-id kyc-profile-id
                                                   :entities       upserts
                                                   :removed-ids    removed-ids
                                                   :snapshot?      snapshot?}]))
              :on-error           (fn [error]
                                    (logging/error "KYC profile scope failed : " kyc-profile-id " >> " error)
                                    (rf/dispatch [::screening-profiles-event/load-failed
                                                  {:kyc-profile-id kyc-profile-id
                                                   :error          error}]))}
             [:div.zap-page.zap-page--overflow
              [status kyc-process]
              (case (:kyc-process/status kyc-process)
                :kyc-process.status/template
                [template-status/view state-atom kyc-process save!]
                :kyc-process.status/draft
                [draft-status/view state-atom local-atom kyc-process save!
                 loading-enrichment-data-atom field-errors-atom]
                :kyc-process.status/prefilled
                [prefilled-status/view state-atom local-atom kyc-process save!]
                :kyc-process.status/wip
                [wip-status/view state-atom kyc-process save! loading-enrichment-data-atom field-errors-atom]
                :kyc-process.status/waiting-for-e-sign
                [waiting-for-esign-status/view state-atom local-atom kyc-process save!]
                :kyc-process.status/esign-in-progress
                [esign-in-progress-status/view state-atom local-atom kyc-process esign-process signatories save!]
                :kyc-process.status/signatures
                [signatures-status/view state-atom local-atom kyc-process save!]
                :kyc-process.status/submitted
                [submitted-status/view state-atom kyc-process save! field-errors-atom]
                :kyc-process.status/approved
                [approved-status/view state-atom kyc-process save! approved-modified-edit-mode?*]
                :kyc-process.status/approved-modified
                [approved-modified-status/view state-atom kyc-process save! approved-modified-edit-mode?* field-errors-atom]
                :kyc-process.status/rejected
                [rejected-status/view state-atom kyc-process save!]
                [unknown-view state-atom kyc-process])]])))})))

(defmethod routing/render-view :entity.type/kyc-process
  [{:keys [route-params _view-params]}]
  (let [kyc-process-id (-> route-params
                           :id
                           js/parseInt)]
    ;;the key change forces rerendering of the component (and its child components)
    ^{:key (str "kyc-process-viewer-id-" kyc-process-id)}
    ;; The KYC sync step sends nothing to a new-grid account. A wait for it
    ;; would end only with the final sync message.
    [syncing-data/wait-until-sync-progress
     (if (session/bulk-synced-entity-type? :entity.type/kyc-process)
       sync-progress/KYC-DATA
       sync-progress/BASE)
     [kyc-process-view {:kyc-process-id kyc-process-id}]]))
